'use client'

/* Adapted from Beautiful UI (MIT): https://www.beautifului.dev/r/thinking-state.json
 * Driven by props (`done`, `steps`) instead of the demo timeline; variants are dropped. */

import { useLayoutEffect, useRef, useState } from 'react'
import { BUI_EASE } from './motion'

/** Agent trace: shimmering label while working, static label once `done`, over an expandable step list.
 *  Finished steps get muted checks; only the current (last) step spins while running. */
export function ThinkingState({
  label = 'Thinking',
  steps = [],
  done = false,
  doneLabel = 'Thought for a moment',
}: {
  label?: string
  steps?: string[]
  done?: boolean
  doneLabel?: string
}) {
  const [manual, setManual] = useState<boolean | null>(null)
  const hasSteps = steps.length > 0
  const expanded = hasSteps && (manual ?? !done)
  const traceRef = useRef<HTMLUListElement>(null)
  const [lineHeight, setLineHeight] = useState(0)
  useLayoutEffect(() => {
    if (traceRef.current) setLineHeight(traceRef.current.offsetHeight)
  }, [steps.length, expanded])

  return (
    <div className="flex w-full max-w-95 flex-col">
      <button
        type="button"
        aria-expanded={hasSteps ? expanded : undefined}
        disabled={!hasSteps}
        onClick={() => setManual(!expanded)}
        className="-mx-1.5 flex w-fit items-center gap-2 rounded-[8px] px-1.5 py-1 transition-colors duration-100 enabled:hover:bg-[var(--hover)] focus-visible:outline-2 focus-visible:outline-[var(--l3-violet)]"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill={done ? 'var(--ink-3)' : 'var(--ink-2)'} aria-hidden className="shrink-0 transition-colors duration-200">
          <path d="M12 2l2.4 7.2L22 12l-7.6 2.8L12 22l-2.4-7.2L2 12l7.6-2.8z" />
        </svg>
        <span role="status" className="contents">
          {done ? (
            <span className="bui-motion text-[13px] font-medium whitespace-nowrap text-[var(--ink-2)]" style={{ animation: 'bui-fade-in 350ms ease-out both' }}>
              {doneLabel}
            </span>
          ) : (
            <span
              className="bui-motion bg-clip-text text-[13px] font-medium whitespace-nowrap text-transparent"
              style={{
                backgroundImage: 'linear-gradient(90deg, var(--ink-3) 35%, var(--ink) 50%, var(--ink-3) 65%)',
                backgroundSize: '200% 100%',
                animation: 'bui-shimmer-text 1.4s linear infinite',
              }}
            >
              {label}
            </span>
          )}
        </span>
        {hasSteps && (
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden
            className="transition-transform duration-300"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)' }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        )}
      </button>
      {hasSteps && (
        <div
          className="bui-motion grid transition-[grid-template-rows,opacity] duration-400"
          style={{ gridTemplateRows: expanded ? '1fr' : '0fr', opacity: expanded ? 1 : 0, transitionTimingFunction: BUI_EASE }}
          aria-hidden={!expanded}
        >
          <div className="overflow-hidden">
            <div className="relative mt-1 ml-[5px] pl-4">
              <span
                aria-hidden
                className="bui-motion absolute left-[3px] w-px bg-[var(--line)]"
                style={{ top: -8, height: lineHeight ? lineHeight - 2 : 0, transition: `height 500ms ${BUI_EASE}` }}
              />
              <ul ref={traceRef} className="flex flex-col gap-1 py-1">
                {steps.map((step, i) => {
                  const finished = done || i < steps.length - 1
                  return (
                    <li
                      key={`${i}-${step}`}
                      className="bui-motion flex min-h-7 items-center gap-2 rounded-[6px] px-1.5 py-0.5"
                      style={{ animation: `bui-fade-up 320ms ${BUI_EASE} ${i * 120}ms both` }}
                    >
                      {finished ? (
                        <svg data-check width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--ink-3)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden className="shrink-0">
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      ) : (
                        <span
                          aria-hidden
                          className="bui-motion size-3 shrink-0 rounded-full border-[1.5px] border-[var(--line-strong)] border-t-[var(--ink-2)]"
                          style={{ animation: 'bui-spin 700ms linear infinite' }}
                        />
                      )}
                      <span className="min-w-0 truncate text-[12.5px] font-medium text-[var(--ink)]">{step}</span>
                    </li>
                  )
                })}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
